import {
    formatPhoneNumber,
    IsoTimestamp,
    PhoneNumberE164,
    SmsConsentStatus,
    TelecomPhoneLineStatus,
} from "@sp-crm/core";
import { fancyPrompt, FancyPromptBehavior } from "components/ui/fancy-prompt";
import { InlineBanner } from "components/ui/inline-banner";
import { defaultLinkStyle } from "components/ui/link";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryButton } from "components/ui/secondary-button";
import { Spinner } from "components/ui/spinner";
import { TextArea } from "components/ui/textarea";
import {
    GetPersonalPhoneNumbersQuery,
    GetSmsConversationQuery,
    SmsDirection,
    useGetSmsConversationQuery,
    useGetTelecomContactStatusQuery,
    useMarkPriorSmsConsentMutation,
    useSendSmsMutation,
} from "generated/graphql";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useProductName } from "store/selectors/branding";
import { usePhoneLines } from "store/selectors/telecom";
import { breakNewlines } from "util/text";
import { handleEvent } from "util/user-events";

interface SmsConversationProps {
    recipient: PhoneNumberE164;
    onClose: () => void;
}

export const SmsConversation: React.FC<SmsConversationProps> = props => {
    const { recipient, onClose } = props;
    const [message, setMessage] = useState("");
    const phoneLines = usePhoneLines();
    const sendSms = useSendSmsMutation();
    const scrollRef = useRef<HTMLDivElement>(null);

    const phoneLineId = useMemo(() => {
        if (phoneLines?.length > 0) {
            return phoneLines[0].id;
        }
        return null;
    }, [phoneLines]);

    const getTelecomContactStatus = useGetTelecomContactStatusQuery(
        {
            request: {
                phoneLineId: phoneLineId,
                phoneNumber: recipient,
            },
        },
        {
            enabled: !!phoneLineId,
            refetchInterval: 30_000,
        },
    );

    const getSmsConversation = useGetSmsConversationQuery(
        {
            request: {
                phoneLineId: phoneLineId,
                phoneNumber: recipient,
            },
        },
        {
            enabled: !!phoneLineId,
            refetchInterval: 30_000,
        },
    );

    const markPriorSmsConsent = useMarkPriorSmsConsentMutation();

    useEffect(() => {
        handleEvent("sms-thread-view", {
            phoneLineId: phoneLineId,
        });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [getSmsConversation.data]);

    const handleSend = useCallback(
        async (e: React.FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            await sendSms.mutateAsync({
                request: {
                    from: phoneLines[0].id,
                    to: recipient,
                    body: message,
                },
            });
            handleEvent("sms-send", {
                phoneLineId: phoneLines[0].id,
                messageLength: message.length,
            });
            setMessage("");
            getSmsConversation.refetch();
        },
        [message, sendSms, phoneLines, getSmsConversation, recipient],
    );

    const handleMessageChange = useCallback(
        (e: React.ChangeEvent<HTMLTextAreaElement>) => {
            setMessage(e.target.value);
        },
        [],
    );

    const handleClose = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            onClose();
        },
        [onClose],
    );

    const handlePriorConsent = useCallback(async () => {
        const date = await fancyPrompt(
            "SMS Consent",
            "Date of consent",
            FancyPromptBehavior.OkCancel,
            "date",
        );

        if (!date) {
            return;
        }

        await markPriorSmsConsent.mutateAsync({
            request: {
                phoneLineId: phoneLineId,
                consentedAt: new Date(date).toISOString() as IsoTimestamp,
                phoneNumber: recipient,
            },
        });
        getTelecomContactStatus.refetch();
    }, [markPriorSmsConsent, recipient, phoneLineId, getTelecomContactStatus]);

    if (!phoneLineId) {
        return (
            <InlineBanner type={"info"}>
                No phone numbers have been configured to send SMS messages. Navigate to{" "}
                <Link className={defaultLinkStyle} to={"/manage/account-settings/phone"}>
                    SMS &amp; Voice Settings
                </Link>{" "}
                to reserve a phone number.
            </InlineBanner>
        );
    }

    const consentStatus =
        getTelecomContactStatus.data?.getTelecomContactStatus.consentStatus;
    const canSend =
        !!getTelecomContactStatus.data?.getTelecomContactStatus.canSend &&
        phoneLines[0].status === TelecomPhoneLineStatus.Active;

    return (
        <div className="flex flex-col h-[calc(100vh-8rem)]">
            <div className="flex-none">
                <div className="text-lg font-medium mb-4">
                    Conversation with {formatPhoneNumber(recipient)}
                </div>
            </div>
            <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto">
                <ConversationHistory data={getSmsConversation.data} />
            </div>
            <div className="flex-none border-t pt-4 mt-4">
                <SendSmsBanner
                    phoneLine={phoneLines[0]}
                    recipient={recipient}
                    consentStatus={consentStatus}
                    onPriorConsent={handlePriorConsent}
                    messages={getSmsConversation.data?.getSmsConversation}
                />
                <form onSubmit={handleSend} className="space-y-4">
                    <TextArea
                        required
                        disabled={!canSend}
                        label="Message"
                        value={message}
                        onChange={handleMessageChange}
                    />
                    <div className="flex items-center space-x-2 justify-end">
                        {sendSms.isLoading ? <Spinner /> : null}
                        <SecondaryButton
                            disabled={sendSms.isLoading}
                            type="button"
                            onClick={handleClose}>
                            Close
                        </SecondaryButton>
                        <PrimaryButton
                            disabled={sendSms.isLoading || !canSend || !message.trim()}
                            type="submit">
                            Send
                        </PrimaryButton>
                    </div>
                </form>
            </div>
        </div>
    );
};

interface SendSmsBannerProps {
    messages: GetSmsConversationQuery["getSmsConversation"];
    phoneLine: GetPersonalPhoneNumbersQuery["getPersonalPhoneNumbers"][number];
    recipient: PhoneNumberE164;
    consentStatus: SmsConsentStatus;
    onPriorConsent: () => void;
}

const SendSmsBanner: React.FC<SendSmsBannerProps> = props => {
    const { messages, phoneLine, recipient, consentStatus, onPriorConsent } = props;
    const productName = useProductName();

    const handlePriorConsent = useCallback(
        async (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            onPriorConsent();
        },
        [onPriorConsent],
    );

    if (phoneLine.status === TelecomPhoneLineStatus.Provisioning) {
        return (
            <InlineBanner type="info">
                Your phone number is still being provisioned, so sending an SMS is not yet
                available. Your phone number should be ready in ten minutes or less.
            </InlineBanner>
        );
    }

    if (
        consentStatus === SmsConsentStatus.NewContact ||
        consentStatus === SmsConsentStatus.None
    ) {
        return (
            <InlineBanner type="warning">
                <div className="space-y-4">
                    <p>
                        This would be your first message to {formatPhoneNumber(recipient)}{" "}
                        from your {productName} phone number. To comply with SMS
                        regulations, you need their consent before sending a message.
                        <br />
                        <br />
                        <ul className="list-disc pl-4">
                            <li>
                                If you obtained consent in writing or through a web form,
                                you can mark it with the button below.
                            </li>
                            <li>
                                If not, have them text &quot;START&quot; to{" "}
                                {formatPhoneNumber(phoneLine.phoneNumber)}.
                            </li>
                        </ul>
                    </p>
                    <SecondaryButton className="text-sm" onClick={handlePriorConsent}>
                        I already have consent
                    </SecondaryButton>
                </div>
            </InlineBanner>
        );
    }

    if (consentStatus === SmsConsentStatus.Unsubscribed) {
        return (
            <InlineBanner type="error">
                <div className="space-y-2">
                    <p>
                        {formatPhoneNumber(recipient)} has unsubscribed from receiving
                        messages from your {productName} phone number. To send an SMS to
                        them, they will need to reply with &quot;START&quot; to
                        re-subscribe.
                    </p>
                </div>
            </InlineBanner>
        );
    }

    if (messages?.length === 0) {
        return (
            <InlineBanner type="info">
                <div className="space-y-2">
                    <p>
                        Since this is your first message to {formatPhoneNumber(recipient)}{" "}
                        from your {productName} phone number, make sure to clearly
                        identify who you are and your purpose for contacting them.
                    </p>
                </div>
            </InlineBanner>
        );
    }

    return null;
};

const ConversationHistory: React.FC<{
    data: GetSmsConversationQuery;
}> = props => {
    const { data } = props;

    if (!data) {
        return null;
    }

    if (data.getSmsConversation.length === 0) {
        return <div className="text-sm text-gray-500">No messages yet</div>;
    }

    return (
        <div className="flex flex-col space-y-2">
            {data.getSmsConversation.map(message => (
                <div
                    key={message.id}
                    className={`flex ${
                        message.direction === "Inbound" ? "justify-start" : "justify-end"
                    }`}>
                    <div
                        className={`max-w-[70%] rounded-lg px-4 py-2 ${
                            message.direction === SmsDirection.Inbound
                                ? "bg-gray-100 text-gray-900"
                                : "bg-blue-500 text-white"
                        }`}>
                        <div className="text-sm">{breakNewlines(message.body)}</div>
                        <div
                            className={`text-xs mt-1 ${
                                message.direction === SmsDirection.Inbound
                                    ? "text-gray-500"
                                    : "text-blue-100"
                            }`}>
                            {new Date(message.dateAdded).toLocaleDateString()}{" "}
                            {new Date(message.dateAdded).toLocaleTimeString()}
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};
